import React, { useState } from 'react';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import { Autocomplete, RenderInputParams } from '@material-ui/lab';
import { TextField, CircularProgress } from '@material-ui/core';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import { IAutoCompleteItem } from '../../typings/autoComplete';
import { EmailTemplateMessageTypeEnum } from '../EmailTemplateRespository';
import { UserFeature, UserFeatureEnum } from '../../types/UserFeature';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
        width: '100%'
    },
  })
);

interface MessageTypeSelectorProps {
    disabled?: boolean;
    required?: boolean;
    multiple?: boolean;
    // tslint:disable-next-line: no-any
    value?: IAutoCompleteItem | any; // value only available in single select
    error?: boolean;
    // tslint:disable-next-line: no-any
    onSelection?: (value: IAutoCompleteItem | IAutoCompleteItem[] | any | any[], name: string) => void;
    className?: string;
    helperText?: string;
    name?: string;
    label?: string;
    disablePortal?: boolean;
    disableCloseOnSelect?: boolean;
    onBlur?: React.FocusEventHandler<HTMLDivElement>;
    variant?: 'standard' | 'outlined' | 'filled';
    size?: 'small' | 'medium';
}

interface MessageTypeSelectorState {
    isLoading: boolean;
    // tslint:disable-next-line: no-any
    options: IAutoCompleteItem[] | any;
}

export const MessageTypeSelector: React.FC<MessageTypeSelectorProps> = props => {

    const classes = useStyles();

    const [state] = useState<MessageTypeSelectorState>({
        isLoading: false,
        options: [
            { label: 'Email', value: EmailTemplateMessageTypeEnum.EMAIL },
            ...(UserFeature.HasAccess(UserFeatureEnum.hasSmsIntegration) 
                ? [{ label: 'SMS', value: EmailTemplateMessageTypeEnum.SMS }] 
                : [])
        ],
    });
    // tslint:disable-next-line: no-any
    const handleChange = (event: React.ChangeEvent<{}>, value: any) => {
        // tslint:disable-next-line: no-console
        if (props.onSelection) {
            props.onSelection(value, props.name ? props.name : 'messageType');
        }
    };

    const getInputProps = (params: RenderInputParams, isLoading?: boolean) => {
        return  ({   
            ...params.InputProps,
            endAdornment: (
                <React.Fragment>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),     
         });
    };

    const normalizedValue = props.multiple
        ? (Array.isArray(props.value) ? props.value : [])
        : (props.value && typeof props.value === 'object' && typeof props.value.label === 'string'
            ? props.value
            : null);

    const getOptionLabel = (option: IAutoCompleteItem | string) => {
        if (typeof option === 'string') {
            return option;
        }

        return option?.label ?? '';
    };

    const getOptionSelected = (option: IAutoCompleteItem, value: IAutoCompleteItem | string) => {
        if (!value || typeof value === 'string') {
            return false;
        }

        return value.value === option.value;
    };

    return (
        <div className={`${classes.root} ${props.className}`}>
            { props.multiple ? 
                (
                    <Autocomplete
                        value={normalizedValue}
                        multiple={true}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.options}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        getOptionLabel={getOptionLabel}
                        getOptionSelected={getOptionSelected}
                        // tslint:disable-next-line: jsx-no-lambda
                        onBlur={props.onBlur}
                        size={props.size ? props.size : 'medium'}
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label ? props.label : 'External Template'}
                                variant={props.variant ? props.variant : 'standard'} 
                                fullWidth={true} 
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}      
                                helperText={props.helperText}              
                                size={props.size ? props.size : 'medium'}        
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                            {part.text}
                                        </span>
                                    ))}
                                </div>
                            );
                        }}
                    />
                )
                :
                (
                    <Autocomplete
                        value={normalizedValue}
                        loading={state.isLoading}
                        disabled={props.disabled}
                        onChange={handleChange}
                        options={state.options}
                        disableCloseOnSelect={props.disableCloseOnSelect}
                        disablePortal={props.disablePortal} // to Stop the dropdown select from closing the form
                        getOptionLabel={getOptionLabel}
                        getOptionSelected={getOptionSelected}
                        onBlur={props.onBlur}
                        size={props.size ? props.size : 'medium'}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderInput={params => (
                            <TextField 
                                {...params} 
                                label={props.label ? props.label : 'External Template'}
                                variant={props.variant ? props.variant : 'standard'} 
                                fullWidth={true} 
                                margin="none" 
                                required={props.required}
                                InputProps={getInputProps(params, state.isLoading)}
                                error={props.error}        
                                helperText={props.helperText}                      
                                size={props.size ? props.size : 'medium'}
                            />
                        )}
                        // tslint:disable-next-line: jsx-no-lambda
                        renderOption={(option, { inputValue }) => {
                            const matches = match(option.label, inputValue);
                            const parts = parse(option.label, matches);
                    
                            return (
                                <div>
                                {parts.map((part, index) => (
                                    <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                    {part.text}
                                    </span>
                                ))}
                                </div>
                            );
                        }}
                    />
                )
            }
        </div>
    );
};